import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PowerSettingsNew as PowerIcon
} from '@mui/icons-material'
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import TechnologyCompanyApi from 'api/b2b/TechnologyCompanyApi'
import OrbitSession from 'hooks/useCookies'

import { TechnologyCompany } from './SearchTechnologyCompany'

// Action dropdown component
const ActionDropdown = ({
  row,
  onStatusUpdate
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: any
  onStatusUpdate: (id: string, newStatus: boolean) => void
}) => {
  const queryClient = useQueryClient()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const company = row.original
  const token = OrbitSession.get('faberToken')
  const navigate = useNavigate()

  const { mutate, isLoading } = useMutation({
    mutationFn: async (nextStatus: boolean) => {
      return TechnologyCompanyApi.updateTechnologyCompanyStatus(
        {
          id: company.id,
          updated_by: OrbitSession.get('email') || '',
          status: nextStatus
        },
        token
      )
    },
    onSuccess: (_res, nextStatus) => {
      // Update the local state through callback
      onStatusUpdate(company.id, nextStatus)

      // Invalidate the cache for search results
      queryClient.invalidateQueries({
        queryKey: ['searchTechnologyCompanies'],
        refetchType: 'none'
      })

      toast.success(
        `Company ${nextStatus ? 'activated' : 'deactivated'} successfully`
      )
    },
    onError: () => {
      toast.error('Failed to update status')
    }
  })

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    navigate(`/hq/b2b/3ptp/add?mode=edit&id=${company.id}`)
    // handleClose()
  }

  const handleToggleStatus = () => {
    mutate(!row.original.isActive)
    handleClose()
  }

  return (
    <>
      <IconButton
        aria-label='more'
        onClick={handleClick}
        size='small'
        disabled={isLoading}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleToggleStatus} disabled={isLoading}>
          <PowerIcon sx={{ mr: 1, fontSize: 18 }} />
          {isLoading
            ? 'Updating...'
            : company.isActive
            ? 'Deactivate'
            : 'Activate'}
        </MenuItem>
      </Menu>
    </>
  )
}

// Status chip component
const StatusChip = ({ status }: { status: boolean }) => {
  return (
    <Chip
      label={status ? 'Active' : 'Inactive'}
      color={status ? 'success' : 'error'}
      size='small'
      variant='filled'
    />
  )
}

export default function TechnologyCompanyTable({
  data,
  setDisplayData
}: {
  data: TechnologyCompany[]
  setDisplayData: React.Dispatch<React.SetStateAction<TechnologyCompany[]>>
}) {
  // Add a force re-render trigger
  const [tableKey, setTableKey] = useState(0)

  // Callback function to update status in parent state
  const handleStatusUpdate = (companyId: string, newStatus: boolean) => {
    setDisplayData((prevState) => {
      const newState = prevState.map((company) => {
        if (company.id === companyId) {
          return { ...company, isActive: newStatus }
        }
        return company
      })
      // Force table re-render
      setTableKey((prev) => prev + 1)
      return newState
    })
  }

  // Column helper for type safety
  const columnHelper = createColumnHelper<TechnologyCompany>()

  // Define columns with the callback
  const columns = [
    columnHelper.accessor('companyName', {
      header: 'Technology Company Name',
      cell: (info) => (
        <Typography variant='body2' fontWeight='medium'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('id', {
      header: 'Technology Company ID',
      cell: (info) => (
        <Typography variant='body2' fontFamily='monospace'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('commissionType', {
      header: 'Commission',
      cell: (info) => (
        <Typography variant='body2' fontWeight='600'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('isActive', {
      header: 'Status',
      cell: (info) => <StatusChip status={info.getValue() || false} />
    }),
    columnHelper.accessor('createdBy', {
      header: 'Created By',
      cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
    }),
    columnHelper.accessor('createdOn', {
      header: 'Created On',
      cell: (info) => (
        <Typography variant='body2' color='text.secondary'>
          {new Date(info.getValue()).toLocaleDateString()}
        </Typography>
      )
    }),
    columnHelper.accessor('updatedBy', {
      header: 'Updated By',
      cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
    }),
    columnHelper.accessor('updatedOn', {
      header: 'Updated On',
      cell: (info) => (
        <Typography variant='body2' color='text.secondary'>
          {new Date(info.getValue()).toLocaleDateString()}
        </Typography>
      )
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <ActionDropdown row={row} onStatusUpdate={handleStatusUpdate} />
      )
    })
  ]

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Force re-render when data changes
    getRowId: (row) => row.id,
    // Enable state management
    enableRowSelection: false
  })

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1} key={tableKey}>
        <Table key={data.length + data.map((d) => d.isActive).join('')}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state */}
        {table.getRowModel().rows.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary'>
              No technology companies found
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  )
}
